/**
 * Fluid simulation smoke — Casa Aura
 *
 * Replaces the particle system with a 2D Navier-Stokes solver producing
 * continuous, volumetric-quality smoke that matches the Three.js blocks
 * fluid reference (https://www.threejs-blocks.com/docs/smoke).
 *
 * Techniques:
 *   — Semi-Lagrangian advection (Stam 2003, unconditionally stable)
 *   — Divergence-free projection via Jacobi iteration
 *   — Multiplicative dissipation (density × 0.978 / velocity × 0.990 per frame)
 *   — Vorticity confinement — amplifies natural curl for wispy tendrils
 *   — Gaussian splat density + velocity injection at the incense tip
 *   — ImageData pixel rendering with bilinear interpolation
 */
(function (root) {
  'use strict';
  if (root.SmokeEffect) return;

  // ── Grid ────────────────────────────────────────────────────────────────────
  var BASE_N   = 64;   // velocity grid width  (halved on mobile)
  var BASE_M   = 96;   // velocity grid height
  var JACOBI   = 20;   // higher iterations → cleaner divergence-free field → stable vorticity
  var VISC     = 0.0;  // near-inviscid air
  var DIFF     = 0.0;  // no spatial diffusion — use dissipation instead
  var DT       = 0.028; // slower timestep — smoother, more reference-like
  var D_DISS   = 0.990; // density dissipation
  var V_DISS   = 0.972; // velocity dissipation
  var CURL_STR = 4;    // mild vorticity — creates natural curl without explosion

  // ── Fluid grid ──────────────────────────────────────────────────────────────
  function Fluid(N, M) {
    this.N = N; this.M = M;
    var sz = (N + 2) * (M + 2);
    // density
    this.d  = new Float32Array(sz);
    this.d0 = new Float32Array(sz);
    // velocity
    this.u  = new Float32Array(sz);
    this.u0 = new Float32Array(sz);
    this.v  = new Float32Array(sz);
    this.v0 = new Float32Array(sz);
    // pressure solve temps
    this.p   = new Float32Array(sz);
    this.div = new Float32Array(sz);
    // vorticity
    this.curl = new Float32Array(sz);
  }

  Fluid.prototype.IX = function (i, j) {
    return i + (this.N + 2) * j;
  };

  // Clamp with boundary reflection
  Fluid.prototype.setBnd = function (b, x) {
    var N = this.N, M = this.M;
    var IX = this.IX.bind(this);
    for (var i = 1; i <= N; i++) {
      x[IX(i, 0)]     = b === 2 ? -x[IX(i, 1)]   : x[IX(i, 1)];
      x[IX(i, M + 1)] = b === 2 ? -x[IX(i, M)]   : x[IX(i, M)];
    }
    for (var j = 1; j <= M; j++) {
      x[IX(0, j)]     = b === 1 ? -x[IX(1, j)]   : x[IX(1, j)];
      x[IX(N + 1, j)] = b === 1 ? -x[IX(N, j)]   : x[IX(N, j)];
    }
    x[IX(0, 0)]         = 0.5 * (x[IX(1, 0)]     + x[IX(0, 1)]);
    x[IX(0, M + 1)]     = 0.5 * (x[IX(1, M + 1)] + x[IX(0, M)]);
    x[IX(N + 1, 0)]     = 0.5 * (x[IX(N, 0)]     + x[IX(N + 1, 1)]);
    x[IX(N + 1, M + 1)] = 0.5 * (x[IX(N, M + 1)] + x[IX(N + 1, M)]);
  };

  // Jacobi linear solver (works for both diffusion and pressure)
  Fluid.prototype.linSolve = function (b, x, x0, a, c) {
    var N = this.N, M = this.M, IX = this.IX.bind(this);
    var invC = 1.0 / c;
    for (var iter = 0; iter < JACOBI; iter++) {
      for (var j = 1; j <= M; j++) {
        for (var i = 1; i <= N; i++) {
          x[IX(i, j)] = (x0[IX(i, j)] + a * (
            x[IX(i-1, j)] + x[IX(i+1, j)] +
            x[IX(i, j-1)] + x[IX(i, j+1)]
          )) * invC;
        }
      }
      this.setBnd(b, x);
    }
  };

  // Semi-Lagrangian advection (Stam)
  Fluid.prototype.advect = function (b, d, d0, u, v) {
    var N = this.N, M = this.M, IX = this.IX.bind(this);
    var dtN = DT * N, dtM = DT * M;
    for (var j = 1; j <= M; j++) {
      for (var i = 1; i <= N; i++) {
        var x = i - dtN * u[IX(i, j)];
        var y = j - dtM * v[IX(i, j)];
        if (x < 0.5)     x = 0.5;
        if (x > N + 0.5) x = N + 0.5;
        if (y < 0.5)     y = 0.5;
        if (y > M + 0.5) y = M + 0.5;
        var i0 = Math.floor(x), i1 = i0 + 1;
        var j0 = Math.floor(y), j1 = j0 + 1;
        var s1 = x - i0, s0 = 1 - s1;
        var t1 = y - j0, t0 = 1 - t1;
        d[IX(i, j)] =
          s0 * (t0 * d0[IX(i0, j0)] + t1 * d0[IX(i0, j1)]) +
          s1 * (t0 * d0[IX(i1, j0)] + t1 * d0[IX(i1, j1)]);
      }
    }
    this.setBnd(b, d);
  };

  // Project velocity field to divergence-free (incompressible)
  Fluid.prototype.project = function () {
    var N = this.N, M = this.M, IX = this.IX.bind(this);
    var hx = 1.0 / N, hy = 1.0 / M;
    for (var j = 1; j <= M; j++) {
      for (var i = 1; i <= N; i++) {
        this.div[IX(i, j)] = -0.5 * (
          hx * (this.u[IX(i+1, j)] - this.u[IX(i-1, j)]) +
          hy * (this.v[IX(i, j+1)] - this.v[IX(i, j-1)])
        );
        this.p[IX(i, j)] = 0;
      }
    }
    this.setBnd(0, this.div);
    this.setBnd(0, this.p);
    this.linSolve(0, this.p, this.div, 1, 4);
    for (var j = 1; j <= M; j++) {
      for (var i = 1; i <= N; i++) {
        this.u[IX(i, j)] -= 0.5 * (this.p[IX(i+1, j)] - this.p[IX(i-1, j)]) / hx;
        this.v[IX(i, j)] -= 0.5 * (this.p[IX(i, j+1)] - this.p[IX(i, j-1)]) / hy;
      }
    }
    this.setBnd(1, this.u);
    this.setBnd(2, this.v);
  };

  // Vorticity confinement — amplifies natural curl to create wispy tendrils
  Fluid.prototype.vorticityConfinement = function () {
    var N = this.N, M = this.M, IX = this.IX.bind(this);
    var i, j, dz, len;
    // Compute curl magnitude at each cell
    for (j = 1; j <= M; j++) {
      for (i = 1; i <= N; i++) {
        this.curl[IX(i, j)] = Math.abs(
          0.5 * (this.v[IX(i+1, j)] - this.v[IX(i-1, j)]) -
          0.5 * (this.u[IX(i, j+1)] - this.u[IX(i, j-1)])
        );
      }
    }
    // Apply force perpendicular to gradient of curl magnitude
    var eps = CURL_STR / N;
    for (j = 2; j <= M - 1; j++) {
      for (i = 2; i <= N - 1; i++) {
        var nx = 0.5 * (this.curl[IX(i+1, j)] - this.curl[IX(i-1, j)]);
        var ny = 0.5 * (this.curl[IX(i, j+1)] - this.curl[IX(i, j-1)]);
        len = Math.sqrt(nx * nx + ny * ny) + 1e-5;
        nx /= len; ny /= len;
        dz = (this.v[IX(i+1, j)] - this.v[IX(i-1, j)]) * 0.5 -
             (this.u[IX(i, j+1)] - this.u[IX(i, j-1)]) * 0.5;
        this.u[IX(i, j)] += eps * ny * dz;
        this.v[IX(i, j)] -= eps * nx * dz;
      }
    }
  };

  // One simulation step
  Fluid.prototype.step = function () {
    var tmp, N = this.N, M = this.M;

    // Apply dissipation
    var sz = (N + 2) * (M + 2);
    for (var k = 0; k < sz; k++) {
      this.u[k] *= V_DISS;
      this.v[k] *= V_DISS;
      this.d[k] *= D_DISS;
    }

    // ── Velocity step ──
    // Swap u→u0, v→v0 (u0/v0 = previous)
    tmp = this.u0; this.u0 = this.u; this.u = tmp;
    tmp = this.v0; this.v0 = this.v; this.v = tmp;

    if (VISC > 0) {
      var a = DT * VISC * N * M;
      this.linSolve(1, this.u, this.u0, a, 1 + 4 * a);
      this.linSolve(2, this.v, this.v0, a, 1 + 4 * a);
    } else {
      // No viscosity: copy previous into current for advect input
      this.u.set(this.u0);
      this.v.set(this.v0);
    }

    this.project();
    tmp = this.u0; this.u0 = this.u; this.u = tmp;
    tmp = this.v0; this.v0 = this.v; this.v = tmp;
    this.advect(1, this.u, this.u0, this.u0, this.v0);
    this.advect(2, this.v, this.v0, this.u0, this.v0);
    this.project();
    this.vorticityConfinement();

    // ── Density step ──
    tmp = this.d0; this.d0 = this.d; this.d = tmp;
    if (DIFF > 0) {
      var b = DT * DIFF * N * M;
      this.linSolve(0, this.d, this.d0, b, 1 + 4 * b);
    } else {
      this.d.set(this.d0);
    }
    tmp = this.d0; this.d0 = this.d; this.d = tmp;
    this.advect(0, this.d, this.d0, this.u, this.v);
  };

  // Gaussian splat — inject density and velocity as a smooth blob
  Fluid.prototype.splat = function (i0, j0, rho, ux, vy, radius) {
    var N = this.N, M = this.M, IX = this.IX.bind(this);
    var r2 = radius * radius;
    var iMin = Math.max(1, Math.floor(i0 - radius * 2));
    var iMax = Math.min(N, Math.ceil(i0  + radius * 2));
    var jMin = Math.max(1, Math.floor(j0 - radius * 2));
    var jMax = Math.min(M, Math.ceil(j0  + radius * 2));
    for (var j = jMin; j <= jMax; j++) {
      for (var i = iMin; i <= iMax; i++) {
        var di = i - i0, dj = j - j0;
        var w = Math.exp(-(di * di + dj * dj) / r2);
        this.d[IX(i, j)] += rho * w;
        this.u[IX(i, j)] += ux  * w;
        this.v[IX(i, j)] += vy  * w;
      }
    }
  };

  // ── SmokeEffect ─────────────────────────────────────────────────────────────

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {Object} [opts]
   * @param {number}  [opts.originOffsetX=0]    Horizontal offset in px from center
   * @param {number}  [opts.originOffsetY=0]    Vertical offset in px from base
   * @param {boolean} [opts.autoplay=true]       IntersectionObserver start/stop
   * @param {boolean} [opts.showEmber=true]      Static glow at incense tip
   * @param {boolean} [opts.screenBlend=false]   Additive blend (best on dark bg)
   */
  function SmokeEffect(canvas, opts) {
    this.canvas = canvas;
    this.ctx    = canvas.getContext('2d');
    this.opts   = Object.assign({
      originOffsetX: 0,
      originOffsetY: 0,
      autoplay:      true,
      showEmber:     true,
      screenBlend:   false,
      // legacy particle params accepted but ignored
      spawnInterval: 2, spawnCount: 3, maxParticles: 160,
    }, opts || {});

    this.fluid   = null;
    this.imgData = null;
    this.running = false;
    this._raf    = null;
    this._frame  = 0;

    this._resize = this._resize.bind(this);
    this._tick   = this._tick.bind(this);

    window.addEventListener('resize', this._resize);
    this._resize();

    if (this.opts.autoplay) this._setupIO();
  }

  SmokeEffect.prototype._resize = function () {
    var parent = this.canvas.parentElement;
    this.canvas.width  = parent.offsetWidth;
    this.canvas.height = parseInt(this.canvas.dataset.height, 10) || 400;

    // Adaptive grid: halve on narrow screens
    var isMobile = this.canvas.width < 580;
    var N = isMobile ? Math.round(BASE_N / 2) : BASE_N;
    var M = isMobile ? Math.round(BASE_M / 2) : BASE_M;

    this.fluid   = new Fluid(N, M);
    this.imgData = this.ctx.createImageData(this.canvas.width, this.canvas.height);

    // Pre-fill image alpha to 0
    var d = this.imgData.data;
    for (var k = 3; k < d.length; k += 4) d[k] = 0;

    // Source position in grid coordinates
    var cW = this.canvas.width, cH = this.canvas.height;
    var oxPx = cW / 2 + this.opts.originOffsetX;
    var oyPx = cH * 0.93  + this.opts.originOffsetY;
    this._srcI = Math.round((oxPx / cW) * N + 0.5);
    this._srcJ = Math.round((oyPx / cH) * M + 0.5);
    this._srcI = Math.max(2, Math.min(N - 1, this._srcI));
    this._srcJ = Math.max(2, Math.min(M - 1, this._srcJ));

    // Store for ember rendering
    this._oxPx = oxPx;
    this._oyPx = oyPx;
  };

  SmokeEffect.prototype._setupIO = function () {
    var self = this;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { e.isIntersecting ? self.start() : self.stop(); });
    }, { threshold: 0.05 });
    io.observe(this.canvas);
  };

  SmokeEffect.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this._raf = requestAnimationFrame(this._tick);
  };

  SmokeEffect.prototype.stop = function () {
    this.running = false;
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
  };

  SmokeEffect.prototype.destroy = function () {
    this.stop();
    window.removeEventListener('resize', this._resize);
  };

  // Inject smoke each frame from the incense tip
  SmokeEffect.prototype._injectSource = function () {
    var f = this.fluid;
    var si = this._srcI, sj = this._srcJ;

    // Density: inject steadily
    f.splat(si, sj, 1.5, 0, 0, 1.2); // density-only splat

    // Velocity: SET (not add) — DT*M*v = displacement per frame
    // With DT=0.020, M=96, v=0.18: 0.020*96*0.18=0.35 cells/frame*4.7px=1.6px/frame=96px/sec
    f.v[f.IX(si,     sj)] = -0.26;
    f.v[f.IX(si - 1, sj)] = -0.18;
    f.v[f.IX(si + 1, sj)] = -0.18;
    if (sj > 1) {
      f.v[f.IX(si, sj - 1)] = -0.14;
    }

    // Tiny random horizontal perturbation (still-air micro-currents)
    f.u[f.IX(si, sj)] += (Math.random() - 0.5) * 0.004;
  };

  // Static soft amber glow at the incense tip — no pulsing
  SmokeEffect.prototype._drawEmber = function () {
    var ctx = this.ctx, ox = this._oxPx, oy = this._oyPx;
    var g = ctx.createRadialGradient(ox, oy, 0, ox, oy, 12);
    g.addColorStop(0,   'rgba(255, 200, 90, 0.14)');
    g.addColorStop(0.6, 'rgba(200, 85, 15, 0.05)');
    g.addColorStop(1,   'rgba(130, 35, 0, 0)');
    ctx.beginPath();
    ctx.fillStyle = g;
    ctx.arc(ox, oy, 12, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.fillStyle = 'rgba(255, 230, 160, 0.45)';
    ctx.arc(ox, oy, 1.0, 0, Math.PI * 2);
    ctx.fill();
  };

  // Render density field to canvas via ImageData (bilinear interpolation)
  SmokeEffect.prototype._render = function () {
    var f   = this.fluid;
    var N   = f.N, M = f.M;
    var cW  = this.canvas.width, cH = this.canvas.height;
    var data = this.imgData.data;

    var scaleX = N / cW, scaleY = M / cH;

    for (var py = 0; py < cH; py++) {
      var gy = py * scaleY + 0.5;
      var j0 = Math.floor(gy), j1 = j0 + 1;
      j0 = Math.max(1, Math.min(M, j0));
      j1 = Math.max(1, Math.min(M, j1));
      var t1 = gy - j0, t0 = 1 - t1;

      var base = py * cW * 4;
      for (var px = 0; px < cW; px++) {
        var gx = px * scaleX + 0.5;
        var i0 = Math.floor(gx), i1 = i0 + 1;
        i0 = Math.max(1, Math.min(N, i0));
        i1 = Math.max(1, Math.min(N, i1));
        var s1 = gx - i0, s0 = 1 - s1;

        var den =
          s0 * (t0 * f.d[f.IX(i0, j0)] + t1 * f.d[f.IX(i0, j1)]) +
          s1 * (t0 * f.d[f.IX(i1, j0)] + t1 * f.d[f.IX(i1, j1)]);

        if (den < 0) den = 0;
        var a = den * 72; // density → alpha
        if (a > 200) a = 200;

        var idx = base + px * 4;
        // Dark grey on light backgrounds; near-white handled via screenBlend
        data[idx]     = 90;  // R
        data[idx + 1] = 92;  // G
        data[idx + 2] = 98;  // B — cool dark grey
        data[idx + 3] = a;
      }
    }

    this.ctx.clearRect(0, 0, cW, cH);

    if (this.opts.screenBlend) {
      this.ctx.globalCompositeOperation = 'screen';
    }

    this.ctx.putImageData(this.imgData, 0, 0);

    if (this.opts.screenBlend) {
      this.ctx.globalCompositeOperation = 'source-over';
    }

    if (this.opts.showEmber) this._drawEmber();
  };

  SmokeEffect.prototype._tick = function () {
    if (!this.running) return;
    this._injectSource();
    this.fluid.step();
    this._render();
    this._raf = requestAnimationFrame(this._tick);
  };

  root.SmokeEffect = SmokeEffect;
  document.dispatchEvent(new CustomEvent('casaaura:smoke-ready'));

}(window));
